import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

export interface Position {
  latitude: number;
  longitude: number;
}

export interface AddressSearchState {
  addressString: string;
  position: Position | null;
  suggestedAddresses: google.maps.places.AutocompletePrediction[];
  activeAutocompleteAddress: google.maps.places.AutocompletePrediction | null;
  isLoadingAutocomplete: boolean;
  autocompleteInputLength: number;
  autocompleteMinLength: number;

  isGoogleMapsReady: boolean;
  isGoogleMapsLoading: boolean;
  googleMapsError: string | null;
}

export interface AddressSearchActions {
  handleAutocompleteSuggestions: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSubmitAutocomplete: (
    address?: google.maps.places.AutocompletePrediction | null,
    callback?: (result: {
      address: google.maps.places.AutocompletePrediction | null;
      position: Position | null;
    }) => void,
  ) => Promise<void>;
  setAddressString: (address: string) => void;
  setPosition: (position: Position | null) => void;
  setActiveAutocompleteAddress: (address: google.maps.places.AutocompletePrediction | null) => void;
  clearSuggestions: () => void;
  selectSuggestion: (suggestion: google.maps.places.AutocompletePrediction) => Promise<void>;
  reset: () => void;
  // New action to manually initialize Google Maps
  initializeGoogleMaps: () => Promise<boolean>;
}

export type UseAddressSearchReturn = AddressSearchState & AddressSearchActions;

interface UseAddressSearchOptions {
  debounceTime?: number;
  minLength?: number;
  maxInitializationAttempts?: number;
  initializationRetryDelay?: number;
  autoInitialize?: boolean;
}

export const useAddressSearch = (options: UseAddressSearchOptions = {}): UseAddressSearchReturn => {
  const {
    debounceTime = 300,
    minLength = 2,
    maxInitializationAttempts = 50,
    initializationRetryDelay = 100,
    autoInitialize = true,
  } = options;

  // Core state
  const [addressString, setAddressString] = useState<string>('');
  const [position, setPosition] = useState<Position | null>(null);
  const [suggestedAddresses, setSuggestedAddresses] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [activeAutocompleteAddress, setActiveAutocompleteAddress] =
    useState<google.maps.places.AutocompletePrediction | null>(null);
  const [isLoadingAutocomplete, setIsLoadingAutocomplete] = useState(false);
  const [autocompleteInputLength, setAutocompleteInputLength] = useState(0);

  // Google Maps initialization state
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);
  const [isGoogleMapsLoading, setIsGoogleMapsLoading] = useState(false);
  const [googleMapsError, setGoogleMapsError] = useState<string | null>(null);

  // Refs
  const debounceRef = useRef<NodeJS.Timeout>();
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService>();
  const geocoderRef = useRef<google.maps.Geocoder>();
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken>();
  const initializationPromiseRef = useRef<Promise<boolean> | null>(null);

  // Helper function to wait for Google Maps to load
  const waitForGoogleMaps = useCallback(
    async (
      maxAttempts: number = maxInitializationAttempts,
      retryDelay: number = initializationRetryDelay,
    ): Promise<boolean> => {
      for (let i = 0; i < maxAttempts; i++) {
        if (window.google?.maps?.places?.AutocompleteService && window.google?.maps?.Geocoder) {
          return true;
        }

        if (i === 0) {
          console.log('⏳ Waiting for Google Maps to load...');
        }

        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }

      console.warn(`⚠️ Google Maps did not load in time (after ${maxAttempts * retryDelay}ms)`);
      return false;
    },
    [maxInitializationAttempts, initializationRetryDelay],
  );

  // Initialize Google Maps services
  const initializeGoogleMaps = useCallback(async (): Promise<boolean> => {
    // Return existing promise if already initializing
    if (initializationPromiseRef.current) {
      return initializationPromiseRef.current;
    }

    // Return true if already ready
    if (isGoogleMapsReady) {
      return true;
    }

    setIsGoogleMapsLoading(true);
    setGoogleMapsError(null);

    initializationPromiseRef.current = (async () => {
      try {
        const isLoaded = await waitForGoogleMaps();

        if (isLoaded) {
          // Initialize services
          autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
          geocoderRef.current = new google.maps.Geocoder();
          sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();

          setIsGoogleMapsReady(true);
          setGoogleMapsError(null);
          console.log('✅ Google Maps services initialized successfully');
          return true;
        } else {
          const errorMessage = 'Google Maps failed to load within the timeout period';
          setGoogleMapsError(errorMessage);
          console.error('❌ ' + errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown Google Maps initialization error';
        setGoogleMapsError(errorMessage);
        console.error('❌ Google Maps initialization error:', error);
        return false;
      } finally {
        setIsGoogleMapsLoading(false);
        initializationPromiseRef.current = null;
      }
    })();

    return initializationPromiseRef.current;
  }, [isGoogleMapsReady, waitForGoogleMaps]);

  // Auto-initialize on mount if enabled
  useEffect(() => {
    if (autoInitialize && !isGoogleMapsReady && !isGoogleMapsLoading) {
      initializeGoogleMaps().catch((error) => {
        console.error('Auto-initialization failed:', error);
      });
    }
  }, [autoInitialize, isGoogleMapsReady, isGoogleMapsLoading, initializeGoogleMaps]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const geocodeAddress = useCallback(
    async (address: string): Promise<Position | null> => {
      // Ensure Google Maps is ready
      if (!isGoogleMapsReady) {
        console.warn('Google Maps not ready for geocoding, attempting to initialize...');
        const initialized = await initializeGoogleMaps();
        if (!initialized) {
          console.error('Cannot geocode: Google Maps initialization failed');
          return null;
        }
      }

      return new Promise((resolve) => {
        if (!geocoderRef.current) {
          console.warn('Google Maps Geocoder not available');
          resolve(null);
          return;
        }

        geocoderRef.current.geocode({ address }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const location = results[0].geometry.location;
            const position = {
              latitude: location.lat(),
              longitude: location.lng(),
            };
            resolve(position);
          } else {
            console.warn('Geocoding failed:', status);
            resolve(null);
          }
        });
      });
    },
    [isGoogleMapsReady, initializeGoogleMaps],
  );

  const performAutocompleteSearch = useCallback(
    async (input: string) => {
      // Ensure Google Maps is ready
      if (!isGoogleMapsReady) {
        console.log('Google Maps not ready, attempting to initialize for autocomplete...');
        const initialized = await initializeGoogleMaps();
        if (!initialized) {
          console.error('Cannot perform autocomplete: Google Maps initialization failed');
          setIsLoadingAutocomplete(false);
          return;
        }
      }

      if (!autocompleteServiceRef.current || !sessionTokenRef.current) {
        console.warn('Google Maps services not available after initialization');
        setIsLoadingAutocomplete(false);
        return;
      }

      const request = {
        input: input.trim(),
        sessionToken: sessionTokenRef.current,
      };

      autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
        setIsLoadingAutocomplete(false);

        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestedAddresses(predictions);
        } else {
          setSuggestedAddresses([]);
          if (status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            console.warn('Autocomplete error:', status);
          }
        }
      });
    },
    [isGoogleMapsReady, initializeGoogleMaps],
  );

  const handleAutocompleteSuggestions = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      setAutocompleteInputLength(input.trim().length);
      setAddressString(input);

      // Clear previous debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Clear suggestions if input is too short
      if (input.length < minLength) {
        setSuggestedAddresses([]);
        setIsLoadingAutocomplete(false);
        return;
      }

      // Set loading state
      setIsLoadingAutocomplete(true);

      // Debounce the search
      debounceRef.current = setTimeout(() => {
        performAutocompleteSearch(input);
      }, debounceTime);
    },
    [debounceTime, minLength, performAutocompleteSearch],
  );

  const handleSubmitAutocomplete = useCallback(
    async (
      address?: google.maps.places.AutocompletePrediction | null,
      callback?: (result: {
        address: google.maps.places.AutocompletePrediction | null;
        position: Position | null;
      }) => void,
    ) => {
      let selectedAddress = address;

      // If no address provided, try to use the first suggestion
      if (!selectedAddress && suggestedAddresses.length > 0) {
        selectedAddress = suggestedAddresses[0];
      }

      let newPosition: Position | null = null;

      // Update state
      if (selectedAddress) {
        setAddressString(selectedAddress.description);
        setActiveAutocompleteAddress(selectedAddress);

        // Geocode to get coordinates
        newPosition = await geocodeAddress(selectedAddress.description);
        if (newPosition) {
          setPosition(newPosition);
        }
      }

      // Clear suggestions
      setSuggestedAddresses([]);
      setAutocompleteInputLength(0);

      // Create new session token for next search (only if Google Maps is ready)
      if (isGoogleMapsReady && window.google?.maps?.places) {
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
      }

      const result = { address: selectedAddress || null, position: newPosition };

      // Call optional callback parameter
      if (callback) {
        callback(result);
      }
    },
    [suggestedAddresses, geocodeAddress, isGoogleMapsReady],
  );

  const clearSuggestions = useCallback(() => {
    setSuggestedAddresses([]);
    setIsLoadingAutocomplete(false);
  }, []);

  const selectSuggestion = useCallback(
    async (suggestion: google.maps.places.AutocompletePrediction) => {
      await handleSubmitAutocomplete(suggestion);
    },
    [handleSubmitAutocomplete],
  );

  const reset = useCallback(() => {
    setAddressString('');
    setPosition(null);
    setSuggestedAddresses([]);
    setActiveAutocompleteAddress(null);
    setIsLoadingAutocomplete(false);
    setAutocompleteInputLength(0);
    // Note: We don't reset Google Maps ready state as it can be reused
  }, []);

  return {
    // State
    addressString,
    position,
    suggestedAddresses,
    activeAutocompleteAddress,
    isLoadingAutocomplete,
    autocompleteInputLength,
    autocompleteMinLength: minLength,

    // New Google Maps state
    isGoogleMapsReady,
    isGoogleMapsLoading,
    googleMapsError,

    // Actions
    handleAutocompleteSuggestions,
    handleSubmitAutocomplete,
    setAddressString,
    setPosition,
    setActiveAutocompleteAddress,
    clearSuggestions,
    selectSuggestion,
    reset,

    // New action
    initializeGoogleMaps,
  };
};
